<template>
    <div>
        <slot v-if="isOfferReady && isSuperOfferFetched"></slot>
        <div
            v-if="isLoading || !isOfferReady || shouldFetchOffer"
            class="spinner-container"
        >
            <SpinnerBars/>
        </div>
    </div>
</template>

<script>
    import { mapActions, mapGetters } from 'vuex';
    import { errorGuard } from '@app/utils';
    import { routeNames } from '@app/router';
    import {
        getSlugFromTimeFilter,
        getTimeFilterFromSlug,
        getTimeFilterForWeekDay,
    } from '@modules/shared/timeFilter/helpers';
    import { ExpandedEventInUrl } from '@app/enums';
    import { TimeFilter } from '@shared/timeFilter/enums';

    export default {
        name: 'OfferContainer',
        props: {
            shouldFetchAll: {
                type: Boolean,
                default: true
            },
            isFetcher: {
                type: Boolean,
                default: true,
            },
            isUrlRedirecter: {
                type: Boolean,
                default: false,
            }
        },
        created() {
            this.isSubscribedToSuperbets = false;
            this.fetchRequirements();
            if (this.shouldUpdateLiveEvents) {
                this.updateLiveEvents();
            }
        },
        watch: {
            isOfferReady(newVal, oldVal) {
                if (this.isFetcher && newVal && !oldVal) {
                    if (!this.areSubscriptionsSet) {
                        this.subscribeToStructChanges();
                        this.subscribeToEventChanges();
                        this.setAreSubscriptionsSet(true);
                    }

                    if (!this.fetchedRequirements) {
                        this.fetchRequirements();
                    }
                }
            },
            async isOfferFetched() {
                if (!this.isUrlRedirecter) {
                    return;
                }
                await this.fetchEventsByIds([this.$route.query[ExpandedEventInUrl.event]]);
                const match = this.eventsMap[this.expandedEventId];
                this.redirectToCorrectUrl(match, this.expandedEventId);
            },
            shouldUpdateLiveEvents(value) {
                if (value) {
                    this.updateLiveEvents();
                }
            },
        },
        computed: {
            ...mapGetters('data/sportOffer', [
                'isOfferReady',
                'isOfferFetched',
                'shouldFetchOffer',
                'areSubscriptionsSet',
                'eventsMap',
                'isSuperOfferFetched',
            ]),
            ...mapGetters('ui/sportOffer/sports', [
                'isLoading',
            ]),
            ...mapGetters('ui/sportOffer', [
                'expandedEventId'
            ]),
            ...mapGetters('navigation', [
                'isOnSportLivePage',
            ]),
            shouldUpdateLiveEvents() {
                return this.isFetcher && this.isOnSportLivePage && this.isOfferReady;
            },
        },
        methods: {
            ...mapActions('data/sportOffer', [
                'fetchTopTenEvents',
                'fetchSuperEvents',
                'subscribeToStructChanges',
                'subscribeToEventChanges',
                'setShouldFetchOffer',
                'setAreSubscriptionsSet',
                'updateLiveEvents',
                'fetchEventsByIds',
            ]),
            ...mapActions('ui/sportOffer/sports', [
                'loadOffer',
            ]),
            async fetchRequirements() {
                if (!this.isFetcher) {
                    return;
                }

                await errorGuard({
                    action: async () => {
                        if (this.shouldFetchAll && this.isOfferReady && this.shouldFetchOffer) {
                            this.fetchedRequirements = true;
                            await this.fetchSuperEvents();
                            await Promise.all([
                                this.loadOffer(),
                                this.fetchTopTenEvents(),
                            ]);
                            this.setShouldFetchOffer(false);
                        }
                    },
                });
            },
            redirectToCorrectUrl(match, eventId) {
                if (!match) {
                    if (eventId) {
                        this.removeQueryParamsFromUrl();
                    }
                    this.isRedirectionFinished = true;
                    return;
                }

                const timeFilter = getTimeFilterFromSlug(this.$route.params.timeFilter);

                if (match.isOngoing()) {
                    const url = this.$router.resolve({
                        name: routeNames.sportOffer,
                        params: {
                            timeFilter: getSlugFromTimeFilter(TimeFilter.live),
                        },
                        query: {
                            ...this.$route.query
                        }
                    }).href;

                    if (timeFilter !== TimeFilter.live) {
                        // we need to note why are we doing it
                        window.location = url;
                    }
                    return;
                }

                const day = getTimeFilterForWeekDay(new Date(match.date).getDay());

                if (day
                    && timeFilter !== day
                    && timeFilter !== TimeFilter.today
                ) {
                    const url = this.$router.resolve({
                        name: routeNames.sportOffer,
                        params: {
                            ...this.$router.currentRoute.params,
                            timeFilter: day,
                        },
                        query: {
                            ...this.$route.query
                        }
                    }).href;

                    window.location = url;
                }
                this.isRedirectionFinished = true;
            },
            removeQueryParamsFromUrl() {
                this.$router.replace({
                    path: this.$route.path,
                });
            },
        },
    };
</script>



// WEBPACK FOOTER //
// src/app/modules/sportOffer/OfferContainer.vue